import React, { useState, useEffect } from 'react';
import { Campaign as CampaignType , Reminder as ReminderType} from '../data/FrontendTypes';
import DateTimePickerComponent from "./DatePicker";
import UserList from "./UserList";
import SettingsIcon from '@mui/icons-material/Settings';
import ReminderList from './ReminderList';
import { useDataContext } from '../data/DataContext';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileDownloadIcon from '@mui/icons-material/FileDownload';      
import { CircularProgress } from '@mui/material';

type CampainProps = {
    campaign: CampaignType;
    setCampaign: (campaign: CampaignType) => void;
    handleDelete: () => void;
}

const Campaign: React.FC<CampainProps> = ({ campaign, setCampaign, handleDelete }: CampainProps) => {
    const [reminderMenuOpened, setReminderMenuOpened] = useState(false);
    const [localCampaign, setLocalCampaign] = useState(campaign);
    
    // Update local state when prop changes
    useEffect(() => {
        setLocalCampaign(campaign);
    }, [campaign]);
    
    const { updateCampaign, downloadCampaignData, dataIsBeingDownloaded } = useDataContext();
    const save = async (campaignToSave: CampaignType = localCampaign) => {
        try {
            await updateCampaign(campaignToSave); // Wait for server update to complete
            setCampaign(campaignToSave); // Only update parent state after server update succeeds
        } catch (error) {
        console.error('Failed to save campaign:', error);
            // Optionally revert local state here if needed
        }
    }

    const handleNameClick = async () => {
        const newName = prompt("Neuer Name:", localCampaign.name);
        if (newName) {
            // Use callback form to ensure we have the latest state
            setLocalCampaign(prevCampaign => {
                const updatedCampaign = { ...prevCampaign, name: newName };
                // Save immediately with the updated campaign
                save(updatedCampaign);
                return updatedCampaign;
            });
        }
    }

    const handleReminderClick = () => {
        setReminderMenuOpened(!reminderMenuOpened);
    }

    if (!localCampaign) {
        return <div>Loading...</div>;
    }

    const handleReminderSave = () => {
        setReminderMenuOpened(false);
        save();
    }

    const handleEndDateChange = (endDate: Date) => {
        const updatedCampaign = { ...localCampaign, endDate };
        setLocalCampaign(updatedCampaign);
        save(updatedCampaign);
    }

    
    return (
        <div className="campaign">
            <h2 onClick={handleNameClick}>{localCampaign.name}</h2>
            <div className="end-date">
                <div className="left">
                    <p>Ende:</p>
                    <DateTimePickerComponent 
                        date={localCampaign.endDate} 
                        setDate={handleEndDateChange}
                    />
                </div>
                <button className="right icon-desc-button clear" onClick={handleReminderClick}>
                    Reminders: {localCampaign.reminders.length}
                    <SettingsIcon className={`settings-icon ${reminderMenuOpened? "activated" : ""}`} />
                </button>
            </div>
            { reminderMenuOpened? 
                <ReminderList 
                    save={handleReminderSave} 
                    reminderList={localCampaign.reminders} 
                    setReminderList={(reminders: ReminderType[]) => setLocalCampaign({...localCampaign, reminders})} 
                /> 
            : 
                <UserList users={localCampaign.userStatuses} />
            }
            <div className="campaign-actions">
                <button 
                    className="icon-desc-button clear" 
                    onClick={handleDelete}  
                >
                    <DeleteForeverIcon/> Löschen
                </button>
                <button className="icon-desc-button" disabled={dataIsBeingDownloaded} onClick={() => downloadCampaignData(localCampaign.id)}>
                    {dataIsBeingDownloaded? <CircularProgress size={20}/> : <FileDownloadIcon/>} Daten exportieren
                </button>
            </div>
        </div>
    );
}


export type { CampaignType };
export default Campaign;